<script context="module" lang="ts">
  type Attrs = {
    src: string;
    alt: string;
    caption: string;
    width: Size;
    align: Alignment;
    uuid: string;
    aspectRatio: number;
    type: string;
    fileName: string;
    ychange?: {
      user: string;
      type: "added" | "removed";
    };
  };
</script>

<script lang="ts">
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import {
    Alignment,
    CSS_ALIGNMENT,
    CSS_SIZE,
    Size,
  } from "$components/editor/extensions/blocks/shared/attributes";
  import Image from "$components/+blocks/Image/Image.svelte";
  import { NodeViewWrapper } from "svelte-tiptap";
  import { fade } from "svelte/transition";
  import { Spinner } from "$components/+blocks/OVIcon";
  import HoverMenu from "$components/editor/extensions/blocks/HoverMenu/HoverMenu.svelte";
  import GutterMenu from "$components/editor/extensions/+shared/GutterMenu.svelte";
  import YChangeTooltip from "../../Collaboration/YChangeTooltip.svelte";
  import type { NodeViewProps } from "@tiptap/core";

  const MIME_GIF = "image/gif";

  export let node: NodeViewProps["node"];
  export let editor: NodeViewProps["editor"];
  export let deleteNode: NodeViewProps["deleteNode"];
  export let updateAttributes: NodeViewProps["updateAttributes"];
  export let isEditable: boolean;
  export let selected: NodeViewProps["selected"];

  $$restProps; // prevent svelte unused props warning

  let type = "";
  let isLoaded = false;
  $: isUploading = node.attrs.isUploading || false;

  let attrs = node.attrs as Attrs;
  let placeholderHeight = 150; // default to 150 pixels whilst loading
  let placeholderWidth = 150;

  type = attrs.type;
  $: ({ src, alt, caption, width, align } = attrs);
  $: alignment = CSS_ALIGNMENT[align];
  $: isGif = type === MIME_GIF;
  // update after initial render
  $: attrs = node.attrs as Attrs;
  $: type = attrs.type;

  const changeSize = (newWidth: string) => {
    width = newWidth as Size;
    updateAttributes({ width: newWidth });
  };

  const setAlign = (newAlign: string) => {
    align = newAlign as Alignment;
    updateAttributes({ align: newAlign });
  };

  const updateAspectRatio = () => {
    // to avoid a divide by zero error
    if (!placeholderWidth || !placeholderHeight) {
      return;
    }
    const aspectRatio = placeholderWidth / placeholderHeight;
    const aspectRatioRounded = Math.floor(aspectRatio * 100) / 100;

    // if there is a change in aspect ratio
    if (attrs.aspectRatio !== aspectRatio && isEditable) {
      // reflect the change back to the node attributes
      updateAttributes({ aspectRatio: aspectRatioRounded });
    }
  };
</script>

<NodeViewWrapper {editor} class="flex {alignment} not-prose group relative my-2 rounded">
  {#if isEditable}
    <GutterMenu on:delete-block={deleteNode} />
  {/if}

  <YChangeTooltip ychange={node.attrs.ychange} />

  {#if src}
    <div
      class="{CSS_SIZE[
        width
      ]} relative w-full rounded-sm ring-offset-gray-100 dark:ring-offset-gray-800"
      style:aspect-ratio={width !== Size.ORIGINAL ? attrs?.aspectRatio : undefined}
      class:min-w-[25%]={width !== Size.ORIGINAL && !isLoaded}
      class:min-h-[150px]={width !== Size.ORIGINAL && !isLoaded}
      bind:clientHeight={placeholderHeight}
      bind:clientWidth={placeholderWidth}
      class:ring={(selected && isEditable) || node.attrs.ychange}
      class:ring-offset-4={node.attrs.ychange}
      class:ring-green-500={node.attrs.ychange?.type == "added"}
      class:ring-red-500={node.attrs.ychange?.type == "removed"}
    >
      <Image
        class="size-full rounded"
        {src}
        on:load={(event) => {
          isLoaded = true;
          // check if the event target a HTMLElement first so we can access the clientHeight & clientWidth values
          const target = event.target instanceof HTMLElement;
          if (target) {
            // get the latest height and width of the image (it may have changed if the user replaced the image)
            placeholderHeight = event.target.clientHeight;
            placeholderWidth = event.target.clientWidth;
            updateAspectRatio();
          }
        }}
        cropPosition={null}
        fallback={isGif || isUploading}
        disableSrcset={width === Size.ORIGINAL}
        allowZoom
        {alt}
      />
      {#if caption}
        <div class="my-1 text-center text-[13px] font-normal text-neutral-500">
          {caption}
        </div>
      {/if}
      {#if isUploading}
        <div transition:fade>
          <div class="absolute inset-0 rounded bg-black/20" />
          <OVIcon class="absolute inset-0 m-auto flex animate-spin" icon={Spinner} size="w-6" />
        </div>
      {/if}
      {#if isEditable}
        <div
          class="absolute inset-x-0 bottom-0 z-[999] mb-2 hidden translate-y-full justify-center group-hover:flex"
        >
          <HoverMenu
            parentType="image"
            {align}
            {width}
            {alt}
            {caption}
            on:sizeChange={(e) => changeSize(e.detail)}
            on:alignChange={(e) => setAlign(e.detail)}
            on:altTextChange={(e) => updateAttributes({ alt: e.detail })}
            on:captionChange={(e) => updateAttributes({ caption: e.detail })}
          />
        </div>
      {/if}
    </div>
  {/if}
</NodeViewWrapper>
